import type { SidebarLanguage } from '@alveolemtl/alveole-components';
import { useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import type { FC } from 'react';
import React, { useCallback, useEffect, useState } from 'react';

import { GetCurrentUserDocument } from '../__generated__/graphql.js';
import type { Language } from '../utils/localization.js';
import { LanguageContext } from './LanguageContext.js';

export { LanguageContext, type LanguageContextType } from './LanguageContext.js';
export const languages: SidebarLanguage[] = [
  { languageCode: 'en', label: 'English' },
  { languageCode: 'fr', label: 'Français' },
];

interface Props {
  children?: React.ReactNode;
}

const LanguageProvider: FC<Props> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');
  const [loading, setLoading] = useState(true);
  const { isLoading, isAuthenticated } = useAuth0();

  const { data: user, loading: isLoadingUser } = useQuery(GetCurrentUserDocument, {
    fetchPolicy: 'cache-first',
    skip: !isAuthenticated,
  });

  useEffect(() => {
    if (isLoading || isLoadingUser) {
      setLoading(true);
      return;
    }

    if (user?.getCurrentUser.language) {
      const langToSet = user.getCurrentUser.language.includes('FR') ? 'fr' : 'en';
      setLoading(false);
      setLanguage(langToSet);
      return;
    }

    // Legacy app used i18nextLng
    const currentSaved = localStorage.getItem('language') ?? localStorage.getItem('i18nextLng');
    if (currentSaved?.includes('fr')) {
      setLoading(false);
      setLanguage('fr');
      return;
    }
    if (currentSaved === 'en') {
      setLoading(false);
      setLanguage('en');
      return;
    }

    const isMarucheSubdomain = window.location.hostname.startsWith('maruche.');
    if (isMarucheSubdomain) {
      setLoading(false);
      setLanguage('fr');
      return;
    }

    setLoading(false);
    setLanguage('en');
  }, [user, setLanguage, isLoading, isLoadingUser]);

  const handleLanguageChange = useCallback((sidebarLanguageCode: Language) => {
    setLanguage(sidebarLanguageCode);
    localStorage.setItem('language', sidebarLanguageCode);
  }, []);

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage: handleLanguageChange,
        loading: loading,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
